const STORAGE_KEY = 'zion_pinned_chats'

export type PinnedChatEntry = {
  contactId: string
  pinnedAt: number
}

function readMap(): Map<string, number> {
  const map = new Map<string, number>()
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return map
    const parsed = JSON.parse(raw) as PinnedChatEntry[] | Record<string, number>
    if (Array.isArray(parsed)) {
      for (const row of parsed) {
        const id = String(row?.contactId || '').trim()
        const at = Number(row?.pinnedAt) || 0
        if (id) map.set(id, at)
      }
    } else if (parsed && typeof parsed === 'object') {
      for (const [id, at] of Object.entries(parsed)) {
        if (id.trim()) map.set(id.trim(), Number(at) || 0)
      }
    }
  } catch {
    /* ignore */
  }
  return map
}

function writeMap(map: Map<string, number>): void {
  const rows: PinnedChatEntry[] = [...map.entries()].map(([contactId, pinnedAt]) => ({
    contactId,
    pinnedAt,
  }))
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rows))
  } catch {
    /* ignore */
  }
}

export function isChatPinned(contactId: string): boolean {
  return readMap().has(contactId.trim())
}

export function getPinnedAt(contactId: string): number | undefined {
  const at = readMap().get(contactId.trim())
  return at && at > 0 ? at : undefined
}

export function setChatPinned(contactId: string, pinned: boolean): void {
  const id = contactId.trim()
  if (!id) return
  const map = readMap()
  if (pinned) map.set(id, Date.now())
  else map.delete(id)
  writeMap(map)
  notifyPinnedChanged()
}

export function toggleChatPinned(contactId: string): boolean {
  const next = !isChatPinned(contactId)
  setChatPinned(contactId, next)
  return next
}

type Listener = () => void
const listeners = new Set<Listener>()

function notifyPinnedChanged(): void {
  for (const fn of listeners) fn()
}

export function onPinnedChatsChanged(fn: Listener): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}
